import React, { useState } from 'react'
import { saveUser, inviteUser } from 'services/users'
import { useNotification } from 'components/Notification'

import { Modal, ModalTitle } from '@the-platform-group/component-library/Modal'
import UserForm from './'
import { useQueryClient } from '@tanstack/react-query'
import { USER_QUERY_KEY } from 'pages/UserDetail/UserDetailComponent'
import { USERS_QUERY_KEY } from 'pages/Users/hooks/useUsers'
import { CUSTOMER_AUTHORIZATION_QUERY_KEY } from 'pages/CustomerAccount/constants'

const userFormDefaults = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
  profileEditingEnabled: true,
  sendInvitation: false,
  timeZone: 'America/Toronto',
  role: 'user',
}

const userFormStateDefault = {
  isEdited: false,
  editingId: null,
  addingUser: false,
}

/**
 * User Edit/Create Form Hook
 * @param {string} customerId - The ID of the customer to create a user for
 * @returns {Object} - An object containing the user form state, functions to handle the form, and the user form modal
 */
function useUserForm(customerId) {
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(false)
  const [values, setValues] = useState(userFormDefaults)
  const [userFormState, setUserFormState] = useState(userFormStateDefault)
  const { createNotification } = useNotification()

  const handleInputChange = e => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setValues({ ...values, [e.target.name]: value })
    setUserFormState({ ...userFormState, isEdited: true })
  }

  async function handleSave(onClose) {
    setIsLoading(true)
    const { password, confirmPassword, email } = values
    const { editingId } = userFormState
    if (password === confirmPassword && email) {
      const userParams = { ...values, id: editingId, ...(customerId && { customerId }) }
      try {
        setUserFormState({ ...userFormState, isEdited: false })

        await saveUser(userParams)

        handleInvalidateUserListQueries()

        onClose()
      } catch (e) {
        if (e.response && e.response.data) {
          displayErrors(e.response.data)
          return
        }

        setValues(() => {
          throw e
        })
      } finally {
        setIsLoading(false)
      }
    }
  }

  function displayErrors(data) {
    if (data.errors) {
      const messages = data.errors.map(e => `${e.field} ${e.message}`).join(',')
      createNotification({ message: messages, variant: 'error' })
    }
  }

  const handleUserEdit = (user = {}) => {
    if (user.id) {
      setValues({ ...user })
      setUserFormState(prev => ({ ...prev, editingId: user.id, isEdited: false }))
    } else {
      setValues({ ...userFormDefaults })
      setUserFormState(prev => ({ ...prev, addingUser: !prev.addingUser }))
    }
  }

  const handleFormClose = () => {
    setUserFormState({ ...userFormStateDefault })
  }

  async function handleInvite() {
    setIsLoading(true)
    try {
      await inviteUser(values.id)
      createNotification({ message: 'Invitation sent successfully!' })
    } catch (e) {
      if (e.response && e.response.data) {
        displayErrors(e.response.data)
        return
      }
    } finally {
      setIsLoading(false)
    }
  }

  const getUserFormModal = () => {
    const { editingId, addingUser, isEdited } = userFormState
    const title = editingId ? `Edit User (id: ${values.id})` : 'Create User'

    return (
      <Modal open={!!editingId || addingUser} onClose={handleFormClose}>
        <ModalTitle title={title} />
        <UserForm
          isLoading={isLoading}
          isFormEdited={isEdited}
          user={values}
          onClose={handleFormClose}
          onChange={handleInputChange}
          onSave={handleSave}
          onInvite={handleInvite}
        />
      </Modal>
    )
  }

  const handleInvalidateUserListQueries = () => {
    // Invalidate User page
    queryClient.invalidateQueries([USER_QUERY_KEY])
    // Invalidate Users page table
    queryClient.invalidateQueries([USERS_QUERY_KEY])
    // Invalidate Account (account page) > Users table
    queryClient.invalidateQueries([CUSTOMER_AUTHORIZATION_QUERY_KEY])
  }

  return {
    isLoading,
    values,
    userFormState,
    handleInputChange,
    handleSave,
    handleUserEdit,
    handleFormClose,
    handleInvite,
    getUserFormModal,
    handleInvalidateUserListQueries,
  }
}

export default useUserForm
